
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, Users, Building2, Database, Layers, GitBranch, FileText, MousePointer, Rocket, Link2, ListOrdered } from "lucide-react";

export default function Leiame() {
  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <BookOpen className="h-8 w-8 text-primary" />
          Leia-me
        </h1>
        <p className="text-muted-foreground">
          Guia completo para usar o Mozarch
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <ListOrdered className="h-5 w-5 text-primary" />
            Índice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <nav className="space-y-2">
            <a href="#conceitos" className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors">
              <BookOpen className="h-4 w-4 text-primary" />
              <span>Antes de começar, entenda alguns conceitos</span>
            </a>
            <a href="#comece-aqui" className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors">
              <FileText className="h-4 w-4 text-primary" />
              <span>Comece por aqui</span>
            </a>
            <a href="#organizacoes" className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors">
              <Building2 className="h-4 w-4 text-primary" />
              <span>Uso de Organizações</span>
            </a>
            <a href="#diagrama" className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors">
              <MousePointer className="h-4 w-4 text-primary" />
              <span>Montando seu Diagrama</span>
            </a>
            <a href="#webhook-deploy" className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors">
              <Rocket className="h-4 w-4 text-primary" />
              <span>Configurar Webhook para Deploy</span>
            </a>
            <a href="#webhook-git" className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors">
              <GitBranch className="h-4 w-4 text-primary" />
              <span>Configurar Webhooks de Git</span>
            </a>
          </nav>
        </CardContent>
      </Card>

      <Card id="conceitos">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Antes de começar, entenda alguns conceitos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <strong className="font-semibold">Subscrição</strong> – Corresponde a uma "conta" ou "assinatura". Tudo o que for configurado ou cadastrado no sistema estará vinculado a uma subscrição.
            </div>
            
            <div>
              <strong className="font-semibold">Dono da Subscrição</strong> – Usuário que criou a subscrição e realizou o upgrade para o plano PRO.
            </div>
            
            <div>
              <strong className="font-semibold">Usuário</strong> – Uma subscrição do tipo PRO permite adicionar novos usuários, que terão acesso ao Mozarch dentro da mesma subscrição.
            </div>
            
            <div>
              <strong className="font-semibold">Organização</strong> – Uma subscrição PRO permite que você crie "Organizações", usadas para agrupar e estruturar seus componentes e diagramas.
            </div>
            
            <div>
              <strong className="font-semibold">Tipo de Componente</strong> – São categorias que representam os elementos da sua arquitetura, como API, Banco de Dados, Back-end, Repositório, Cache etc. Essa definição é totalmente personalizável.
            </div>
            
            <div>
              <strong className="font-semibold">Componente</strong> – Representa uma parte ou elemento do seu sistema. É a informação central do Mozarch. Por exemplo: um componente chamado "API Busca CEP" do tipo "API".
            </div>
            
            <div>
              <strong className="font-semibold">Diagrama</strong> – Representação visual das conexões entre seus componentes.
            </div>
            
            <div>
              <strong className="font-semibold">Relacionamento entre Componentes</strong> – Toda vez que dois componentes são conectados, um relacionamento é automaticamente criado entre eles.
            </div>
            
            <div>
              <strong className="font-semibold">Detalhe de um Componente</strong> – Cada componente possui uma tela de detalhe que exibe suas informações, histórico (linha do tempo) e documentação.
            </div>
            
            <div>
              <strong className="font-semibold">Linha do Tempo</strong> – Mostra todos os eventos relevantes de um componente: criação, atualizações de documentação, push/merge requests no repositório (se configurado via webhook) e deploys (também via webhook).
            </div>
          </div>
        </CardContent>
      </Card>

      <Card id="comece-aqui">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Comece por aqui</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="space-y-3 list-disc list-inside">
            <li>Cadastre os tipos de componentes importantes para sua arquitetura. Dê um nome e escolha um ícone representativo.</li>
            <li>Depois, cadastre seus componentes com base nesses tipos.</li>
            <li>Com os componentes criados, você pode começar a montar seus diagramas, conectando os elementos.</li>
            <li>O editor de diagramas sempre abrirá em uma nova aba para facilitar o seu trabalho.</li>
            <li>Se preferir, também é possível criar novos componentes diretamente no diagrama.</li>
            <li>Lembre-se de salvar o diagrama após finalizá-lo.</li>
            <li>Ao criar um diagrama, os relacionamentos entre componentes são registrados automaticamente, o que garante rastreabilidade e visibilidade completa da sua arquitetura.</li>
          </ul>
        </CardContent>
      </Card>

      <Card id="organizacoes">
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Uso de Organizações
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>Se você estiver usando o plano PRO, poderá organizar seus componentes e diagramas por Organizações.</p>
          
          <div>
            <h4 className="font-semibold text-lg mb-2">O que é uma Organização?</h4>
            <p>É uma subdivisão dentro da subscrição que permite agrupar componentes e diagramas. Você pode utilizar as Organizações para representar áreas da empresa (ex: Financeiro, Operações, Vendas), times (ex: Equipe Checkout, Equipe Produtos) ou processos de negócio (ex: Aquisição, Cadastro, Customer Success).</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p><strong>Importante:</strong> Componentes e diagramas estão vinculados a uma Organização. Já os usuários e os tipos de componentes são definidos no nível da subscrição, e não da Organização.</p>
          </div>
        </CardContent>
      </Card>

      <Card id="diagrama">
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <MousePointer className="h-5 w-5 text-primary" />
            Montando seu Diagrama
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p>Depois de criar um novo diagrama, você pode clicar no ícone de "diagrama" para abrir (em nova aba) o "Editor de Diagrama do Mozarch". Ali, você poderá arrastar componentes e montar o desenho do seu diagrama.</p>
          </div>
          
          <div>
            <h4 className="font-semibold text-lg mb-3">Barra Lateral</h4>
            <p>A barra lateral exibe os tipos de componentes cadastrados na sua subscrição, com seus respectivos ícones. Se ainda não houver nenhum, volte à etapa de cadastro.</p>
          </div>
          
          <div>
            <h4 className="font-semibold text-lg mb-3">Arrastando Componentes</h4>
            <p>Arraste um tipo de componente para o diagrama. Isso adiciona um componente "vazio" ao desenho, ainda sem vínculo com um componente real.</p>
          </div>
          
          <div>
            <h4 className="font-semibold text-lg mb-3">Editando as Propriedades</h4>
            <p>Clique com o botão direito no componente e selecione "Editar".</p>
            
            <p className="mt-2">No formulário que aparece na lateral direita, escolha entre criar um novo componente ou vincular um já existente:</p>
            
            <ul className="mt-3 space-y-2 list-disc list-inside ml-4">
              <li>Para criar um novo, selecione "Novo Componente" e informe o nome.</li>
              <li>Para associar um componente existente, selecione a Organização e, em seguida, o Componente desejado.</li>
            </ul>
            
            <p className="mt-3">Se optar por criar um novo componente, ele será automaticamente adicionado à sua lista após salvar o diagrama.</p>
          </div>
        </CardContent>
      </Card>

      <Card id="webhook-deploy">
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            Configurar Webhook para Deploy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            O Mozarch pode receber notificações automáticas sempre que um deploy for realizado em seus componentes. 
            Essas notificações serão registradas na linha do tempo do componente, permitindo rastrear todo o histórico de deployments.
          </p>
          
          <div>
            <h4 className="font-semibold text-lg mb-2">Como configurar</h4>
            <p className="mb-3">
              Para integrar o webhook de deploy ao seu pipeline de CI/CD, adicione uma chamada HTTP POST para a URL abaixo 
              após a conclusão do seu processo de deploy:
            </p>
            
            <div className="bg-muted p-3 rounded-lg font-mono text-sm mb-3">
              https://ejoivwzxdketcbvoeddu.supabase.co/functions/v1/webhook_deploy
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-lg mb-2">Payload esperado</h4>
            <p className="mb-3">O webhook espera receber um JSON com a seguinte estrutura:</p>
            
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto">
{`{
  "component_id": "mozarch-api",
  "deploy_date": "2025-11-10T22:15:30.000Z",
  "details": {
    "version": "1.4.2",
    "commit": "a8b930f",
    "environment": "production"
  }
}`}
            </pre>
          </div>
          
          <div>
            <h4 className="font-semibold text-lg mb-2">Campos do payload</h4>
            <ul className="space-y-2 list-disc list-inside ml-4">
              <li><strong>component_id</strong>: Identificador único do componente que recebeu o deploy. Você pode copiar esse ID diretamente da lista de componentes usando o ícone de copiar ao lado de cada item.</li>
              <li><strong>deploy_date</strong>: Data e hora do deploy no formato ISO 8601 (ex: 2025-11-10T22:15:30.000Z).</li>
              <li><strong>details</strong> (opcional): Objeto contendo informações adicionais sobre o deploy:
                <ul className="ml-6 mt-2 space-y-1 list-disc list-inside">
                  <li><strong>version</strong>: Versão da aplicação deployada.</li>
                  <li><strong>commit</strong>: Hash do commit associado ao deploy.</li>
                  <li><strong>environment</strong>: Ambiente de destino (ex: production, staging, development).</li>
                </ul>
              </li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p><strong>Dica:</strong> Encontre o <code className="bg-white px-2 py-1 rounded">component_id</code> rapidamente na lista de componentes. 
            Basta clicar no ícone de copiar ao lado de cada componente para copiar o ID para a área de transferência.</p>
          </div>
        </CardContent>
      </Card>

      <Card id="webhook-git">
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-primary" />
            Configurar Webhooks de Git
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p>
            O Mozarch pode receber eventos de pull requests/merge requests diretamente do Bitbucket ou GitHub. 
            Esses eventos serão automaticamente registrados na linha do tempo dos componentes, facilitando o acompanhamento 
            de mudanças no código.
          </p>
          
          <div>
            <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              Bitbucket
            </h4>
            
            <div className="space-y-3">
              <p><strong>URL do webhook:</strong></p>
              <div className="bg-muted p-3 rounded-lg font-mono text-sm mb-3">
                https://ejoivwzxdketcbvoeddu.supabase.co/functions/v1/webhook_bitbucket_event
              </div>
              
              <p><strong>Como configurar:</strong></p>
              <ol className="space-y-2 list-decimal list-inside ml-4">
                <li>Acesse as configurações do seu repositório no Bitbucket.</li>
                <li>Navegue até a seção "Webhooks" no menu lateral.</li>
                <li>Clique em "Add webhook" para criar um novo webhook.</li>
                <li>Cole a URL fornecida acima no campo "URL".</li>
                <li>Em "Triggers", marque apenas a opção: <strong>Pull request merged</strong></li>
                <li>Salve o webhook.</li>
              </ol>
              
              <div className="bg-amber-50 p-3 rounded-lg border-l-4 border-amber-500 mt-3">
                <p><strong>Importante:</strong> Para que os eventos sejam corretamente associados ao componente, 
                certifique-se de preencher o campo <code className="bg-white px-2 py-1 rounded">repo_name</code> no cadastro do componente 
                com o nome completo do repositório (ex: workspace/repo-name).</p>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              GitHub
            </h4>
            
            <div className="space-y-3">
              <p><strong>URL do webhook:</strong></p>
              <div className="bg-muted p-3 rounded-lg font-mono text-sm mb-3">
                https://ejoivwzxdketcbvoeddu.supabase.co/functions/v1/webhook_github_events
              </div>
              
              <p><strong>Como configurar:</strong></p>
              <ol className="space-y-2 list-decimal list-inside ml-4">
                <li>Acesse as configurações do seu repositório no GitHub.</li>
                <li>Clique em "Settings" e depois em "Webhooks" no menu lateral.</li>
                <li>Clique em "Add webhook" para criar um novo webhook.</li>
                <li>Cole a URL fornecida acima no campo "Payload URL".</li>
                <li>Em "Content type", selecione <strong>application/json</strong>.</li>
                <li>Em "Which events would you like to trigger this webhook?", selecione "Let me select individual events".</li>
                <li>Marque apenas a opção: <strong>Pull requests</strong></li>
                <li>Certifique-se de que a opção "Active" está marcada.</li>
                <li>Clique em "Add webhook" para salvar.</li>
              </ol>
              
              <div className="bg-amber-50 p-3 rounded-lg border-l-4 border-amber-500 mt-3">
                <p><strong>Importante:</strong> Para que os eventos sejam corretamente associados ao componente, 
                certifique-se de preencher o campo <code className="bg-white px-2 py-1 rounded">repo_name</code> no cadastro do componente 
                com o nome completo do repositório (ex: owner/repo-name).</p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p><strong>Dica:</strong> Após configurar os webhooks, você verá todos os eventos de merge/pull requests 
            aparecendo automaticamente na linha do tempo dos componentes vinculados aos repositórios.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
